import React from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';

const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header with Close Button */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Privacy Policy</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-grow overflow-y-auto px-6 py-4">
          <div className="space-y-4 text-sm text-gray-700">
            <p>
              This Privacy Policy explains how Ordinaa ("Company", "we", "us", or "our") collects, uses, discloses, and protects information when you access or use our software applications, platforms, and services (collectively, the "Services").
            </p>

            <p className="font-semibold">
              We are committed to protecting your privacy and handling data in a transparent, secure, and responsible manner.
            </p>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">1. Scope of This Policy</h3>
              <p className="mb-2">This Privacy Policy applies to:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Visitors to our website</li>
                <li>Customers and authorized users of our SaaS platforms</li>
                <li>Prospective customers and partners</li>
              </ul>
              <p className="mt-2">It does not apply to third-party websites or services that may be linked from our Services.</p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">2. Information We Collect</h3>
              <p className="mb-2">We collect information only to the extent necessary to provide and improve our Services.</p>
              <h4 className="font-semibold text-gray-900 mb-1">2.1 Information You Provide</h4>
              <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
                <li>Name, business email address, phone number</li>
                <li>Organization name, role, and account credentials</li>
                <li>Support requests and communications</li>
                <li>Configuration data and workflow inputs</li>
              </ul>
              <h4 className="font-semibold text-gray-900 mb-1">2.2 Customer Content</h4>
              <p className="mb-3">Our Services may process documents, emails, records, and other data uploaded or generated by customers ("Customer Data").</p>
              <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
                <li>Customer Data remains the sole property of the customer.</li>
                <li>Ordinaa acts strictly as a data processor, not a data owner.</li>
              </ul>
              <h4 className="font-semibold text-gray-900 mb-1">2.3 Automatically Collected Information</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>IP address, device type, browser information</li>
                <li>Log data, usage metrics, and performance data</li>
                <li>Cookies and similar technologies (see Section 9)</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">3. How We Use Information</h3>
              <p className="mb-2">We use collected information to:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Provide, operate, and maintain the Services</li>
                <li>Authenticate users and manage accounts</li>
                <li>Process workflows and deliver platform functionality</li>
                <li>Improve product performance and reliability</li>
                <li>Respond to inquiries and provide customer support</li>
                <li>Ensure security, auditability, and operational integrity</li>
                <li>Meet contractual and legal obligations</li>
              </ul>
              <p className="mt-2">We do not sell personal data.</p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">4. Legal Basis for Processing (GDPR)</h3>
              <p className="mb-2">Where applicable, personal data is processed based on:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Performance of a contract</li>
                <li>Legitimate business interests</li>
                <li>Legal obligations</li>
                <li>User consent, where required</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">5. Data Sharing and Disclosure</h3>
              <p className="mb-2">We may share information only in the following circumstances:</p>
              <h4 className="font-semibold text-gray-900 mb-1">5.1 Service Providers</h4>
              <p className="mb-3">With trusted subprocessors (such as cloud hosting, monitoring, and communications providers) under strict confidentiality and data protection agreements.</p>
              <h4 className="font-semibold text-gray-900 mb-1">5.2 Legal Requirements</h4>
              <p className="mb-3">When required to comply with applicable laws, regulations, legal processes, or governmental requests.</p>
              <h4 className="font-semibold text-gray-900 mb-1">5.3 Business Transfers</h4>
              <p>In connection with a merger, acquisition, or sale of assets, subject to continued data protection safeguards.</p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">6. Data Security</h3>
              <p className="mb-2">Ordinaa implements industry-standard security measures, including:</p>
              <ul className="list-disc list-inside space-y-1 ml-2 mb-2">
                <li>Encryption of data in transit and at rest</li>
                <li>Role-based access controls</li>
                <li>Secure cloud infrastructure</li>
                <li>Audit logging and monitoring</li>
                <li>Periodic security reviews</li>
              </ul>
              <p>While we take reasonable steps to protect data, no method of transmission or storage is completely secure.</p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">7. Data Retention</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Personal data is retained only as long as necessary for the purposes outlined</li>
                <li>Customer Data retention is governed by contractual agreements</li>
                <li>Upon service termination, Customer Data is deleted or returned as agreed</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">8. International Data Transfers</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Your information may be processed in countries other than your own.</li>
                <li>Where required, appropriate safeguards are implemented to protect such data.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">9. Cookies and Tracking Technologies</h3>
              <p className="mb-2">We use cookies and similar technologies to:</p>
              <ul className="list-disc list-inside space-y-1 ml-2 mb-2">
                <li>Enable essential site functionality</li>
                <li>Improve performance and user experience</li>
                <li>Analyze usage trends</li>
              </ul>
              <p>You can control cookie usage through your browser settings.</p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">10. Your Rights</h3>
              <p className="mb-2">Depending on your jurisdiction, you may have rights to:</p>
              <ul className="list-disc list-inside space-y-1 ml-2 mb-2">
                <li>Access your personal data</li>
                <li>Request correction or deletion</li>
                <li>Restrict or object to processing</li>
                <li>Request data portability</li>
              </ul>
              <p>Requests may be submitted using the contact details below.</p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">11. Children's Privacy</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Our Services are intended for business use only and are not directed at individuals under 18 years of age.</li>
                <li>We do not knowingly collect personal data from children.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">12. Changes to This Policy</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>We may update this Privacy Policy from time to time.</li>
                <li>Any material changes will be reflected on this page with an updated effective date.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">13. Contact Information</h3>
              <p className="mb-2">For questions or concerns regarding this Privacy Policy or our data practices, contact:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Email: contact@ordinaa.com</li>
                <li>Phone: +91 9380470079</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Contact Us Button - Bottom */}
        <div className="px-6 pb-4 pt-2 border-t border-gray-200">
          <Link
            to="/contact"
            className="w-full px-4 py-2 bg-cyan-600 text-white rounded-lg font-semibold hover:bg-cyan-700 transition-colors text-center text-sm block"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyModal;
